import {
  selectCitationSchema,
  selectRunSchema,
  selectTaskDocumentSchema,
  selectTaskSchema,
  selectWorkflowDocumentSchema,
  selectWorkflowSchema,
  selectWorkflowVersionSchema,
} from "@repo/db/schema";
import { PostmarkEmailSchema } from "@repo/shared";
import {
  ActionResultSchema,
  NodeDataSchema,
  ResultOutputSchema,
  RunSchema,
  WorkflowSchema,
} from "@repo/shared/workflow";
import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { zu } from "zod_utilz";

const c = initContract();

/**
 * Error Schema for standardized error responses
 */
const ErrorSchema = z.object({
  message: z.string(),
});

/**
 * Pagination parameters and response shapes
 */
const PaginationQuerySchema = z
  .object({
    offset: z.number().int().min(0).default(0).optional(),
    limit: z.number().int().min(1).max(100).default(10).optional(),
  })
  .optional();

const PaginatedResponseSchema = z.object({
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

// TODO(yogi): Fix this type when right type is known
const FileSchema = z.custom<unknown>(() => true);

const GetTasksResponseItemSchema = selectTaskSchema.extend({
  latestRun: selectRunSchema.optional(),
  workflow: selectWorkflowSchema.pick({ id: true, name: true }),
});

export type GetTasksResponseItem = z.infer<typeof GetTasksResponseItemSchema>;

const TaskRunDisplaySchema = z.object({
  run: RunSchema,
  documents: z.array(
    selectTaskDocumentSchema.extend({
      signedUrl: z.string().optional(),
      citations: z.array(selectCitationSchema),
    }),
  ),
});

const workflowsRouter = c.router({
  getAll: {
    method: "GET",
    path: "/workflows",
    query: PaginationQuerySchema,
    responses: {
      200: PaginatedResponseSchema.extend({
        items: z.array(
          selectWorkflowSchema.extend({
            currentVersion: selectWorkflowVersionSchema,
          }),
        ),
      }),
      403: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Get paginated list of workflows",
  },
  getById: {
    method: "GET",
    path: "/workflows/:workflowId",
    pathParams: z.object({ workflowId: z.string().uuid() }),
    responses: {
      200: z.object({
        workflow: WorkflowSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Get a single workflow by ID",
  },
  create: {
    method: "POST",
    path: "/workflows",
    body: z.object({
      name: z.string().min(1),
    }),
    responses: {
      200: z.object({
        workflow: WorkflowSchema,
      }),
      403: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Create a new workflow",
  },
  duplicate: {
    method: "POST",
    path: "/workflows/:workflowId/duplicate",
    pathParams: z.object({ workflowId: z.string().uuid() }),
    body: z.object({
      name: z.string().min(1),
    }),
    responses: {
      200: z.object({
        workflow: WorkflowSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Duplicate an existing workflow",
  },
  delete: {
    method: "DELETE",
    path: "/workflows/:workflowId",
    pathParams: z.object({ workflowId: z.string().uuid() }),
    body: z.object({}),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Delete a workflow by ID",
  },
  getDocument: {
    method: "GET",
    path: "/workflows/:workflowId/document/:documentId",
    pathParams: z.object({
      workflowId: z.string().uuid(),
      documentId: z.string().uuid(),
    }),
    responses: {
      200: z.object({
        signedUrl: z.string(),
        document: selectWorkflowDocumentSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Get a signed URL to load a document",
  },
  getNode: {
    method: "GET",
    path: "/workflows/:workflowId/node/:nodeId",
    pathParams: z.object({
      workflowId: z.string().uuid(),
      nodeId: z.string().uuid(),
    }),
    responses: {
      200: z.object({
        node: NodeDataSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Get a node by ID",
  },
  createNode: {
    method: "POST",
    path: "/workflows/:workflowId/node",
    pathParams: z.object({
      workflowId: z.string().uuid(),
    }),
    body: z.object({
      data: NodeDataSchema,
      groupId: z.string().uuid().optional(),
    }),
    responses: {
      200: z.object({
        node: NodeDataSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Create a new node in a workflow",
  },
  updateNode: {
    method: "POST",
    path: "/workflows/:workflowId/node/:nodeId",
    pathParams: z.object({
      workflowId: z.string().uuid(),
      nodeId: z.string().uuid(),
    }),
    body: z.object({
      update: NodeDataSchema,
    }),
    responses: {
      200: z.object({
        updatedNode: NodeDataSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Edit a node in a given workflow",
  },
  deleteNode: {
    method: "DELETE",
    path: "/workflows/:workflowId/node/:nodeId",
    pathParams: z.object({
      workflowId: z.string().uuid(),
      nodeId: z.string().uuid(),
    }),
    // NOTE(yogi): Declaring empty body to fix typing bug
    // https://github.com/ts-rest/ts-rest/issues/712
    body: z.object({}),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Delete a node from a workflow",
  },
  reparentNode: {
    method: "POST",
    path: "/workflows/:workflowId/node/:nodeId/reparent",
    pathParams: z.object({
      workflowId: z.string().uuid(),
      nodeId: z.string().uuid(),
    }),
    body: z.object({
      oldGroupId: z.string().uuid(),
      newGroupId: z.string().uuid(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Reparent a node to a new group",
  },
  createExtractionModelFromFile: {
    method: "POST",
    path: "/workflows/:workflowId/create-extraction-model-from-file",
    contentType: "multipart/form-data",
    pathParams: z.object({ workflowId: z.string().uuid() }),
    body: z.object({
      file: FileSchema,
    }),
    responses: {
      200: z.object({
        nodes: z.array(NodeDataSchema),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary:
      "Create an extraction model from a document. Note this is a pre-DAG editor endpoint. Returns the nodes that were created.",
  },
  updateExtractionModelFile: {
    method: "POST",
    path: "/workflows/:workflowId/update-extraction-model-file",
    contentType: "multipart/form-data",
    pathParams: z.object({ workflowId: z.string().uuid() }),
    body: z.any(),
    responses: {
      200: z.object({
        updatedNode: NodeDataSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Update the example document for an extraction model",
  },
  uploadExportExcelTemplate: {
    method: "POST",
    path: "/workflows/:workflowId/upload-export-excel-template",
    contentType: "multipart/form-data",
    pathParams: z.object({ workflowId: z.string().uuid() }),
    body: z.any(),
    responses: {
      200: z.object({
        updatedNode: NodeDataSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Upload an Excel template for an export excel sheet action",
  },
});

const tasksRouter = c.router({
  getAll: {
    method: "GET",
    path: "/tasks",
    query: PaginationQuerySchema,
    responses: {
      200: PaginatedResponseSchema.extend({
        items: z.array(GetTasksResponseItemSchema),
      }),
      403: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Get paginated list of tasks",
  },
  getById: {
    method: "GET",
    path: "/tasks/:taskId",
    pathParams: z.object({ taskId: z.string().uuid() }),
    responses: {
      200: selectTaskSchema.extend({
        runs: z.array(TaskRunDisplaySchema),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Get a task and its runs by ID",
  },
  retry: {
    method: "POST",
    path: "/tasks/:taskId/retry",
    pathParams: z.object({ taskId: z.string().uuid() }),
    body: z.object({}),
    responses: {
      200: z.object({
        status: z.enum([
          "Already Processing",
          "Missing Entities",
          "Successfully Retrying",
        ]),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Retry a task with the entities from the latest run",
  },
  create: {
    method: "POST",
    path: "/tasks",
    contentType: "multipart/form-data",
    // Issue with getting this typed correctly here
    // Body should have name, workflowVersionId, and then the files
    body: z.any(),
    responses: {
      200: z.object({
        success: z.boolean(),
        task: selectTaskSchema,
      }),
      403: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Create a new task from a workflow",
  },
  previewAction: {
    method: "POST",
    path: "/tasks/:taskId/preview-action",
    pathParams: z.object({ taskId: z.string().uuid() }),
    body: z.object({
      runId: z.string().uuid(),
      nodeId: z.string().uuid(),
    }),
    responses: {
      200: z.object({
        result: ActionResultSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Preview an action for a task",
  },
  runAction: {
    method: "POST",
    path: "/tasks/:taskId/run-action",
    pathParams: z.object({ taskId: z.string().uuid() }),
    body: z.object({
      runId: z.string().uuid(),
      nodeId: z.string().uuid(),
      resultId: z.string().uuid().optional(),
      preview: z.boolean().optional().default(true),
    }),
    responses: {
      200: z.object({
        result: ActionResultSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Run an action for a task",
  },
  updateRunStatus: {
    method: "POST",
    path: "/tasks/:taskId/update-run-status",
    pathParams: z.object({ taskId: z.string().uuid() }),
    body: z.object({
      status: RunSchema.shape.status,
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Update the status of the latest run for a task",
  },
  updateRPASessionStatus: {
    method: "POST",
    path: "/tasks/:taskId/rpa/session-status",
    pathParams: z.object({ taskId: z.string().uuid() }),
    body: z.object({
      action: z.enum(["pause", "resume"]),
      resultId: z.string().uuid(),
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Update the run status of a RPA run",
  },
  getResult: {
    method: "GET",
    path: "/tasks/:taskId/result/:resultId",
    pathParams: z.object({
      taskId: z.string().uuid(),
      resultId: z.string().uuid(),
    }),
    responses: {
      200: z.object({
        result: ResultOutputSchema,
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
  },
  updateResult: {
    method: "POST",
    path: "/tasks/:taskId/update-result",
    pathParams: z.object({
      taskId: z.string().uuid(),
    }),
    body: z.object({
      resultId: z.string().uuid(),
      update: ResultOutputSchema,
    }),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
      403: ErrorSchema,
      404: ErrorSchema,
      500: ErrorSchema,
    },
    summary: "Update a result for a task",
  },
});

export const workflowsContract = workflowsRouter;
export const tasksContract = tasksRouter;

export const publicAPIContract = c.router({
  externalAPI: c.router({
    createTask: {
      method: "POST",
      path: "/v1/task",
      contentType: "multipart/form-data",
      body: z.object({
        metadata: zu
          .stringToJSON()
          .pipe(
            z.object({
              workflow_id: z.string(),
              task_id: z.string(),
              files: z.array(
                z.object({
                  name: z.string(),
                  model: z.string(),
                }),
              ),
            }),
          )
          // Convert to camelCase for consistency
          .transform((val) => ({
            workflowId: val.workflow_id,
            taskId: val.task_id,
            files: val.files,
          })),
      }),
      responses: {
        200: z.object({
          task: z.object({
            id: z.string(),
            name: z.string(),
          }),
        }),
        400: ErrorSchema,
        500: ErrorSchema,
      },
      summary: "Create a new task from an external API",
    },
  }),
  webhooks: c.router({
    postmark: c.router({
      createTask: {
        method: "POST",
        path: "/webhooks/postmark/create-task",
        body: PostmarkEmailSchema,
        responses: {
          200: z.object({
            success: z.boolean(),
          }),
          400: ErrorSchema,
          500: ErrorSchema,
        },
        summary: "Handles Postmark inbound webhook to create a task",
      },
    }),
  }),
});

export const internalAPIContract = c.router({
  rpa: c.router({
    updateStatus: {
      method: "POST",
      path: "/api/action-executions/:actionExecutionId/update-status",
      pathParams: z.object({ actionExecutionId: z.string() }),
      body: z.object({
        status: z.enum(["COMPLETED", "FAILED"]),
        error: z.string().optional(),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
        400: ErrorSchema,
        500: ErrorSchema,
      },
      summary: "Update the end status of a RPA run",
    },
    notifyHistory: {
      method: "POST",
      path: "/api/action-executions/:actionExecutionId/update-history",
      pathParams: z.object({ actionExecutionId: z.string() }),
      body: z.object({
        history: z.string(), // unclear why this is coming as a string from the RPA service
        error: z.string().optional(),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
        }),
        400: ErrorSchema,
        500: ErrorSchema,
      },
      summary: "Update the history of a RPA run",
    },
  }),
});

export const apiContract = c.router({
  public: publicAPIContract,
  internal: internalAPIContract,
  private: c.router({
    workflows: workflowsContract,
    tasks: tasksContract,
  }),
});
